import {AppUrls} from 'shared/config/model/urls';
import {Pages} from 'shared/config/model/pages';

import React, {useState} from 'react';
import {i18n} from '@lingui/core';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import BackButton from 'shared/ui/IconButton/BackButton';
import {EditButton, DeleteButton, PauseButton, PlayButton} from 'shared/ui/IconButton/iconButtons';
import MoreActionsDots from 'shared/ui/MoreActions/MoreActionsDots';
import MoreActionsItem from 'shared/ui/MoreActions/MoreActionsItem';
import IDomainMonitorOutput from 'features/domain/model/DomainMonitor/IDomainMonitor';

import {MonitorTagsModal} from 'entities/monitor';

import useUpdateDomainMonitorTags from 'features/domain/model/useUpdateDomainMonitorTags';

import DomainMonitorModal from '../../DomainMonitorModal';
import DomainMonitorPauseModal from '../../DomainMonitorPauseModal/DomainMonitorPauseModal';
import DomainMonitorResumeModal from '../../DomainMonitorResumeModal/DomainMonitorResumeModal';
import DomainMonitorDeleteModal from '../../DomainMonitorDeleteModal/DomainMonitorDeleteModal';

import styles from './DomainMonitorHeader.module.scss';

type ModalType = 'edit' | 'delete' | 'pause' | 'resume' | 'tags' | null;

interface IDomainMonitorHeaderProps {
    domainMonitor: IDomainMonitorOutput,
}

const DomainMonitorHeader: React.FC<IDomainMonitorHeaderProps> = ({domainMonitor}) => {
    const [activeModal, setActiveModal] = useState<ModalType>(null);

    const handleCloseModal = () => setActiveModal(null);

    return (
        <div className={styles.container}>
            <div className={styles.titleBlock}>
                <BackButton to={AppUrls.DOMAIN_MONITORS} text={t`Назад`} className={styles.backButton}/>
                <h3>{i18n._(Pages.DOMAIN_MONITOR_DETAILS)}</h3>
                <div className={styles.name} title={domainMonitor.domain_human_readable}>
                    «{domainMonitor.domain_human_readable}»
                </div>
                <EditButton
                    title={t`Редактировать`}
                    iconSize={16}
                    onClick={() => setActiveModal('edit')}
                    className={styles.hideMob}
                />
            </div>
            <div className={styles.actionBlock}>
                <div className={styles.actionButtons}>
                    {
                        domainMonitor.active
                            ? <PauseButton
                                className={cn(styles.actionButton, styles.pause)}
                                title={t`На паузу`}
                                iconSize={16}
                                text={t`На паузу`}
                                onClick={() => setActiveModal('pause')}
                            />
                            : <PlayButton
                                className={cn(styles.actionButton, styles.play)}
                                title={t`Возобновить`}
                                iconSize={16}
                                text={t`Возобновить`}
                                onClick={() => setActiveModal('resume')}
                            />
                    }
                    <DeleteButton
                        className={styles.deleteButton}
                        title={t`Удалить`}
                        iconSize={16}
                        onClick={() => setActiveModal('delete')}
                    />
                </div>
                <div className={styles.menu}>
                    <MoreActionsDots vertical>
                        {
                            domainMonitor.active
                                ? <MoreActionsItem
                                    label={t`На паузу`}
                                    onClick={() => setActiveModal('pause')}
                                />
                                : <MoreActionsItem
                                    label={t`Возобновить`}
                                    onClick={() => setActiveModal('resume')}
                                />
                        }
                        <MoreActionsItem
                            label={t`Редактировать`}
                            onClick={() => setActiveModal('edit')}
                        />
                        <MoreActionsItem
                            label={t`Теги`}
                            onClick={() => setActiveModal('tags')}
                        />
                        <MoreActionsItem
                            label={t`Удалить`}
                            onClick={() => setActiveModal('delete')}
                        />
                    </MoreActionsDots>
                </div>
            </div>

            <DomainMonitorModal item={domainMonitor} open={activeModal === 'edit'} handleClose={handleCloseModal}/>
            <DomainMonitorPauseModal
                item={domainMonitor}
                open={activeModal === 'pause'}
                handleClose={handleCloseModal}
            />
            <DomainMonitorResumeModal
                item={domainMonitor}
                open={activeModal === 'resume'}
                handleClose={handleCloseModal}
            />
            <MonitorTagsModal
                uuid={domainMonitor.uuid}
                tags={domainMonitor.tags}
                useUpdateTags={useUpdateDomainMonitorTags}
                open={activeModal === 'tags'}
                handleClose={handleCloseModal}
            />
            <DomainMonitorDeleteModal
                item={domainMonitor}
                open={activeModal === 'delete'}
                handleClose={handleCloseModal}
            />
        </div>
    );
};

export default DomainMonitorHeader;
